import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import Eyebrow from './Eyebrow';
import Heading from './Heading';
import MarginNote from './MarginNote';
import SiteImage from './SiteImage';

/**
 * The interior call-out fill: peach 23% into (rule 51% into paper), i.e. 234 / 217 / 200 —
 * the package's `.page-callout` colour within 1 RGB value, built from the brand tokens
 * only. Opaque, so the paper grid does not show through.
 */
export const PEACH_CALLOUT_BG =
  'bg-[color-mix(in_srgb,theme(colors.brand.peach)_23%,color-mix(in_srgb,theme(colors.brand.rule)_51%,theme(colors.brand.paper)))]';

/**
 * The closing call-out band of a page, inset from the canvas edge (15px, 24px from 800px).
 * - `navy` (default): the homepage closing band — centred, display-size heading, sheet
 *   text, orange `<em>` accent (4.62:1 on navy), actions side by side from 800px (stacked
 *   and max 320px wide below). Put `variant="inverse"` / `"ghost"` Buttons in it.
 * - `peach`: the interior "page callout" — heading and text left, actions stacked on the
 *   right from 800px, a 4–5px orange top edge and a dashed stitch along the bottom.
 *   Its fill is opaque (the paper grid does not show through): PEACH_CALLOUT_BG, the
 *   package's call-out colour mixed from tokens. Muted text on it is 4.55:1, rust 4.63:1,
 *   navy 11.67:1. Use primary/secondary Buttons.
 *   It is a `.site-light` surface (rust focus ring); the navy band is `.site-dark` (peach).
 *
 * Props:
 * - `title` (required): heading content, may contain `<em>` / `<br />`
 * - `headingLevel`: 2 (default) | 3 | 4
 * - `titleId`: heading id (default from useId); the section is labelled by it
 * - `tone`: 'navy' (default) | 'peach'
 * - `accent`: `<em>` colour (default 'orange' on navy, 'rust' on peach) — see Heading
 * - `eyebrow`: string (rendered as an Eyebrow) or your own node
 * - `text`: paragraph under the heading
 * - `actions`: node — Buttons
 * - `icon`: optional decorative node shown first (peach tone; e.g. a 46px rust lucide icon)
 * - `note`: optional handwritten Caveat note under the actions (navy tone; decorative,
 *   aria-hidden, shown at every width like the reference's closing note)
 * - `image`: optional photo id (navy tone; e.g. 'HOME-CTA-01'): a decorative background
 *   under an 80% navy overlay (upgrade brief §3.1 item 10), lazy, empty alt
 * - `className`, `children` (extra content after the text), other props go on the section.
 */
export default function CtaBand({
  title,
  headingLevel = 2,
  titleId,
  tone = 'navy',
  accent,
  eyebrow,
  text,
  actions,
  icon,
  note,
  image,
  className,
  children,
  ...rest
}) {
  const autoId = useId();
  const headingId = titleId || `site-cta-${autoId}`;
  const navy = tone !== 'peach';
  const hasEyebrow = eyebrow !== undefined && eyebrow !== null && eyebrow !== false && eyebrow !== '';
  const eyebrowNode = hasEyebrow && !React.isValidElement(eyebrow)
    ? <Eyebrow tone={navy ? 'sheet' : 'inherit'}>{eyebrow}</Eyebrow>
    : eyebrow;

  if (navy) {
    return (
      <section
        aria-labelledby={headingId}
        data-tone="navy"
        className={cn(
          'site-dark relative mx-[15px] overflow-hidden bg-navy px-5 py-14 text-center text-brand-sheet nav:mx-6 nav:px-page nav:pb-[4.625rem] nav:pt-20',
          className,
        )}
        {...rest}
      >
        {image ? (
          <>
            <SiteImage id={image} ratio="21x9" fill decorative tone="navy" sizes="(min-width: 1808px) 1680px, 100vw" />
            <span aria-hidden="true" className="pointer-events-none absolute inset-0 bg-navy/80" />
          </>
        ) : null}
        <div className={image ? 'relative' : undefined}>
        {hasEyebrow ? eyebrowNode : null}
        <Heading
          level={headingLevel}
          size="display"
          accent={accent || 'orange'}
          id={headingId}
          className={cn('mx-auto mb-[2.1875rem]', hasEyebrow ? 'mt-[1.6875rem]' : 'mt-0')}
        >
          {title}
        </Heading>
        {text ? <p className="mx-auto -mt-3 mb-8 max-w-[36.875rem] text-base leading-relaxed text-brand-sheet/80 nav:text-[1.0625rem]">{text}</p> : null}
        {children}
        {actions ? (
          <div className="mx-auto flex max-w-xs flex-col items-stretch gap-[15px] nav:max-w-none nav:flex-row nav:justify-center">
            {actions}
          </div>
        ) : null}
        {note ? (
          <MarginNote tone="sheet" tilt={-4} hideOnMobile={false} className="mx-auto mt-[2.0625rem] block max-w-[19.375rem]">
            {note}
          </MarginNote>
        ) : null}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby={headingId}
      data-tone="peach"
      className={cn(
        'site-light relative mx-[15px] border-t-4 border-brand-orange px-[25px] py-[45px] text-navy shadow-[0_6px_0] shadow-navy/5',
        PEACH_CALLOUT_BG,
        'nav:mx-6 nav:flex nav:items-center nav:justify-between nav:gap-10 nav:border-t-[5px] nav:px-page nav:py-[70px]',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="mb-4 inline-flex shrink-0 text-brand-rust nav:mb-0 [&_svg]:size-[46px]">
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 nav:flex-1">
        {hasEyebrow ? eyebrowNode : null}
        <Heading level={headingLevel} size="section" accent={accent || 'rust'} id={headingId} className="mb-5 mt-3">
          {title}
        </Heading>
        {text ? <p className="max-w-[66ch] text-brand-muted">{text}</p> : null}
        {children}
      </div>
      {actions ? (
        <div className="mt-[30px] flex max-w-[25rem] flex-col items-stretch gap-3.5 nav:mt-0 nav:shrink-0">
          {actions}
        </div>
      ) : null}
      <span aria-hidden="true" className="site-stitch pointer-events-none absolute inset-x-0 -bottom-px h-[7px]" />
    </section>
  );
}
